import type { FC } from 'react';
import React from 'react';

import {
	LoadableLazy,
	LoadablePaint,
	LoadableBackground,
	LoadableAfterPaint,
	LoadingPriority,
} from '@confluence/loadable';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';

import { InlineCommentsLoading } from './view-mode/InlineCommentsLoading.tsx';
import type { InlineCommentsProps } from './view-mode/InlineComments.tsx';
import { FOCUSED_INLINE_COMMENT_METRIC } from './perf.config.ts';

/* View Mode Exports */
const InlineCommentsLoaderPaint = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-inline-comments" */ './view-mode/InlineComments.tsx'
			)
		).InlineComments,
	loading: InlineCommentsLoading as any,
});

const InlineCommentsLoaderLazy = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-inline-comments" */ './view-mode/InlineComments.tsx'
			)
		).InlineComments,
	loading: InlineCommentsLoading as any,
});

const InlineCommentsLoaderAfterPaint = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-inline-comments" */ './view-mode/InlineComments.tsx'
			)
		).InlineComments,
});

const InlineCommentsLoaderBackground = LoadableBackground({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-inline-comments" */ './view-mode/InlineComments.tsx'
			)
		).InlineComments,
});

const loaders = {
	[LoadingPriority.LAZY]: InlineCommentsLoaderLazy,
	[LoadingPriority.PAINT]: InlineCommentsLoaderPaint,
	[LoadingPriority.AFTER_PAINT]: InlineCommentsLoaderAfterPaint,
};

export const InlineCommentsLoader: FC<
	InlineCommentsProps & {
		loadingPriority: LoadingPriority;
		focusedCommentId?: string;
	}
> = ({ loadingPriority, focusedCommentId, ...rest }) => {
	const CommentsLoader = loaders[loadingPriority] || InlineCommentsLoaderBackground;

	return (
		<>
			{focusedCommentId && (
				<PageSegmentLoadStart
					key={`start-${focusedCommentId}`}
					metric={FOCUSED_INLINE_COMMENT_METRIC}
				/>
			)}
			<CommentsLoader {...rest} />
		</>
	);
};
